import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  // Divider,
  Form,
  // Heading,
  Text,
  TextField,
  useDialogContainer,
  Tabs,
  TabList,
  TabPanels,
  Item,
  Flex,
  TextArea,
  Checkbox,
  ComboBox,
  useAsyncList,
  ToggleButton,
} from "@adobe/react-spectrum";
import { useOktaAuth } from "@okta/okta-react";
import React, { useEffect, useState } from "react";
import {
  error as ErrorToast,
  success as SuccessToast,
} from "@react/react-spectrum/Toast";
import PropTypes from "prop-types";
import useUserProfile from "../../../../context/user-context";
// import LoadingDialog from "../../LoadingDialog";
import { gtmhApi } from "../../../../api/gtmhApi";

export default function CreateParentSubDialog({
  isPageLoading,
  setPageLoading,
  refreshTable,
  status,
}) {
  const dialog = useDialogContainer();
  const { user } = useUserProfile();
  const { authState } = useOktaAuth();

  const [createType, setCreateType] = useState("parent");
  const [createRequest, setCreateRequest] = useState({
    sub_name: "",
    duns_number: "0",
    domain: "0",
    parent_id: "",
    government: false,
    diversified: false, // parent creation
    sanctioned: false, // sub creation
    comment: "",
    user_id: user?.userId,
    user_name: user?.name,
    status,
  });
  const [errorObj, setErrorObj] = useState({});
  const [isIdSearch, setIsIdSearch] = useState(false);
  const [isNameSearch, setIsNameSearch] = useState(false);
  const [subName, setSubName] = useState("");
  const [validNameError, setValidNameError] = useState(null);

  const tabs = [
    { id: "parent", name: "Create New Parent" },
    { id: "sub", name: "Create New Subsidiary" },
  ];

  const targetAccountList = useAsyncList({
    async load({ filterText }) {
      if (!filterText)
        return {
          items: [],
        };
      const json = await gtmhApi.searchParents(
        {
          search_string: isIdSearch || isNameSearch ? "" : filterText,
          sub_id: !isIdSearch ? "" : filterText,
          sub_name: !isNameSearch ? "" : filterText,
          status: status === "active" ? ["active"] : ["active", "in-review"],
          parent_only: true,
        },
        authState.accessToken
      );
      const results = json?.results[0].results || [];
      return {
        items: results,
      };
    },
  });

  const createParent = () => {
    setPageLoading(true);
    gtmhApi
      .createParent(createRequest, authState.accessToken)
      .then((data) => {
        if (data.results?.results) {
          dialog.dismiss();
          refreshTable();
          setPageLoading(false);
          SuccessToast(
            `Parent with id ${data.results?.results[0].sub_id} successfully created!`,
            { timeout: 5000 }
          );
        } else {
          setPageLoading(false);
          // setErrorMessage(data.results?.error_payload.message);
          const errorList = JSON.parse(
            data.results?.error_payload.error_description
          );
          const errorObject = {};
          errorList.forEach((err) => {
            errorObject[err.attribute] = err.message;
          });
          setErrorObj(errorObject);
        }
      })
      .catch((error) => {
        console.log("error");
        dialog.dismiss();
        setPageLoading(false);
        ErrorToast(error.response?.data?.message || "Error Creating Parent", {
          timeout: 5000,
        });
      });
  };

  const createSub = () => {
    setPageLoading(true);
    gtmhApi
      .createSub(createRequest, authState.accessToken)
      .then((data) => {
        if (data.results?.results) {
          dialog.dismiss();
          refreshTable();
          setPageLoading(false);
          SuccessToast(
            `Sub with id ${
              data.results?.results[0].subs.find(
                (sub) => sub.sub_name === createRequest.sub_name
              ).sub_id
            } successfully created!`,
            { timeout: 5000 }
          );
        } else {
          setPageLoading(false);
          // setErrorMessage(data.results?.error_payload.message);
          const errorList = JSON.parse(
            data.results?.error_payload.error_description
          );
          const errorObject = {};
          errorList.forEach((err) => {
            errorObject[err.attribute] = err.message;
          });
          setErrorObj(errorObject);
        }
      })
      .catch((error) => {
        console.log("error");
        dialog.dismiss();
        setPageLoading(false);
        ErrorToast(error.response?.data?.message || "Error Creating Sub", {
          timeout: 5000,
        });
      });
  };

  const handleCancel = () => {
    dialog.dismiss();
  };

  useEffect(() => {
    setCreateRequest({
      sub_name: "",
      duns_number: "0",
      domain: "0",
      parent_id: "",
      government: false,
      diversified: false, // parent creation
      sanctioned: false, // sub creation
      comment: "",
      user_id: user?.userId,
      user_name: user?.name,
      status,
    });
    setErrorObj({});
    setValidNameError(null);
    setSubName("");
  }, [createType]);

  const checkIsValidName = (name) => {
    setPageLoading(true);
    if (name === "") {
      setValidNameError(null);
      setPageLoading(false);
      return true;
    }
    gtmhApi
      .fetchParentSubs(
        {
          sub_name: name.trim(),
          status: [
            "active",
            "in-review",
            "rejected",
            "deleted",
            "pending-deletion",
          ],
          page_no: 1,
          page_size: 10,
          exact_match: true,
          sort_options: [{ field_name: "parent_id", sort_order: 1 }],
        },
        authState.accessToken
      )
      .then((data) => {
        if (data.results.results.length === 0) {
          setValidNameError(null);
          setPageLoading(false);
          return true;
        }
        const existingSub = data.results?.results[0]?.subs.find(
          (x) => x.sub_name === name.trim()
        );
        setValidNameError(
          `There is an existing sub with the same name (${existingSub?.sub_id} - status: ${existingSub?.status})`
        );
        setPageLoading(false);
        return false;
      })
      .catch((error) => {
        console.log("error: ", error);
        setPageLoading(false);
        return false;
      });
    // setPageLoading(false);
    return false;
  };

  const renderCreateParentForm = () => (
    <Flex direction="column" width="100%">
      <Form
        labelPosition="side"
        width="100%"
        marginTop="size-10"
        gap="10px"
        validationBehavior="native"
        validationErrors={errorObj}
      >
        <TextField
          autoFocus
          label="Name"
          name="sub_name"
          labelAlign="end"
          onChange={(val) => {
            setSubName(val);
            setCreateRequest({ ...createRequest, sub_name: val.trim() });
          }}
          onFocusChange={() => checkIsValidName(subName)}
          value={subName}
          isRequired
          validationState={validNameError ? "invalid" : undefined}
          errorMessage={validNameError}
        />
        <TextField
          label="Domain"
          labelAlign="end"
          name="domain"
          defaultValue="0"
          onChange={(val) =>
            setCreateRequest({ ...createRequest, domain: val.trim() })
          }
          isRequired
        />
        <TextField
          label="HQ DUNS"
          labelAlign="end"
          name="duns_number"
          defaultValue="0"
          onChange={(val) =>
            setCreateRequest({ ...createRequest, duns_number: val.trim() })
          }
          isRequired
        />
        <TextArea
          label="Comment"
          labelAlign="end"
          onChange={(val) =>
            setCreateRequest({ ...createRequest, comment: val })
          }
        />
      </Form>
      <Flex direction="column" marginStart="170px" marginTop="10px">
        <Checkbox
          onChange={(val) =>
            setCreateRequest({ ...createRequest, diversified: val })
          }
        >
          Diversified Company
        </Checkbox>
      </Flex>
    </Flex>
  );

  const renderCreateSubForm = () => (
    <Flex direction="column" width="100%">
      <Form
        labelPosition="side"
        width="100%"
        marginTop="size-10"
        gap="10px"
        validationBehavior="native"
        validationErrors={errorObj}
      >
        {/* <TextField
          autoFocus
          label="Name"
          name="sub_name"
          labelAlign="end"
          onChange={(val) =>
            setCreateRequest({ ...createRequest, sub_name: val })
          }
          isRequired
        /> */}
        <TextField
          autoFocus
          label="Name"
          name="sub_name"
          labelAlign="end"
          onChange={(val) => {
            setSubName(val);
            setCreateRequest({ ...createRequest, sub_name: val.trim() });
          }}
          onFocusChange={() => checkIsValidName(subName)}
          value={subName}
          isRequired
          validationState={validNameError ? "invalid" : undefined}
          errorMessage={validNameError}
        />
        <TextField
          label="Domain"
          name="domain"
          defaultValue="0"
          labelAlign="end"
          onChange={(val) =>
            setCreateRequest({ ...createRequest, domain: val.trim() })
          }
          isRequired
        />
        <TextField
          label="HQ DUNS"
          name="duns_number"
          defaultValue="0"
          labelAlign="end"
          onChange={(val) =>
            setCreateRequest({ ...createRequest, duns_number: val.trim() })
          }
          isRequired
        />
        <ComboBox
          label="Parent"
          labelAlign="end"
          isRequired
          inputValue={targetAccountList.filterText}
          selectedKey={createRequest.parent_id || null}
          onInputChange={(val) => {
            targetAccountList.setFilterText(val);
            // If user starts typing, clear selected parent
            setCreateRequest({ ...createRequest, parent_id: "" });
          }}
          onSelectionChange={(val) => {
            const selected = targetAccountList.items.find(
              (item) => item.sub_id === val
            );
            if (selected) {
              setCreateRequest({
                ...createRequest,
                parent_id: val.substring(0, val.length - 3),
              });
              targetAccountList.setFilterText(selected.sub_name); // show display name
            }
          }}
          items={targetAccountList.items}
          loadingState={targetAccountList.loadingState}
          description={
            <Flex marginTop="-2px" marginBottom="-15px">
              <Flex height="32px" alignItems="center">
                <Flex height="32px" alignItems="center" gap="5px">
                  <ToggleButton
                    isSelected={isNameSearch}
                    onChange={setIsNameSearch}
                    isDisabled={isIdSearch}
                    height="24px"
                  >
                    Name Search
                  </ToggleButton>
                  <ToggleButton
                    isSelected={isIdSearch}
                    onChange={setIsIdSearch}
                    isDisabled={isNameSearch}
                    height="24px"
                  >
                    ID Search
                  </ToggleButton>
                </Flex>
              </Flex>
            </Flex>
          }
        >
          {(item) =>
            item && (
              <Item key={item.sub_id}>
                <Text>{item.sub_name}</Text>
              </Item>
            )
          }
        </ComboBox>
        <TextArea
          autoFocus
          label="Comment"
          labelAlign="end"
          onChange={(val) =>
            setCreateRequest({ ...createRequest, comment: val })
          }
        />
      </Form>
      <Flex direction="column" marginStart="152px" marginTop="10px">
        <Checkbox
          onChange={(val) =>
            setCreateRequest({ ...createRequest, sanctioned: val })
          }
        >
          Sanctioned Company
        </Checkbox>
      </Flex>
    </Flex>
  );

  return (
    //   <Dialog height="295px">
    <Dialog size="L" width="50rem" height="100%">
      <Content>
        <Flex direction="column">
          <Tabs
            aria-label="Parent + Subsidiary Accounts"
            margin="size-100"
            marginTop="size-0"
            width="90%"
            direction="row"
            items={tabs}
            defaultSelectedKey={createType}
            onSelectionChange={(val) => setCreateType(val)}
          >
            <TabList flex="1 1 auto">
              {(item) => <Item>{item.name}</Item>}
            </TabList>
            <TabPanels>
              {(item) => (
                <Item id={item.id}>
                  <Flex
                    marginTop="size-50"
                    UNSAFE_style={{
                      backgroundColor: "white",
                      borderRadius: "0.5rem",
                    }}
                    direction="row"
                    // height={
                    //   list.items > 0 ? "static-size-7000" : "static-size-6000"
                    // }
                  >
                    {item.id === "parent"
                      ? renderCreateParentForm()
                      : renderCreateSubForm()}
                  </Flex>
                </Item>
              )}
            </TabPanels>
          </Tabs>
          <Flex direction="column" alignItems="end">
            <ButtonGroup>
              <Button onPress={handleCancel} variant="secondary">
                Cancel
              </Button>
              <Button
                onPress={() =>
                  createType === "parent" ? createParent() : createSub()
                }
                color="primary"
                variant="accent"
                isDisabled={
                  createRequest.sub_name === "" ||
                  createRequest.duns_number === "" ||
                  createRequest.domain === "" ||
                  (createType === "sub" && createRequest.parent_id === "") ||
                  validNameError !== null ||
                  isPageLoading
                }
              >
                {`Create ${status === "in-review" ? "for Review" : ""}`}
              </Button>
            </ButtonGroup>
          </Flex>
        </Flex>
      </Content>
    </Dialog>
  );
}

CreateParentSubDialog.propTypes = {
  status: PropTypes.string.isRequired,
  refreshTable: PropTypes.func.isRequired,
  setPageLoading: PropTypes.func.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
};
